import { RoleGate } from "@/common/components/layout/RoleGate";
import { Scale } from "lucide-react";
import { useState } from "react";
import { useWeightHistory } from "../api/animalsApi";
import type { AnimalDetail } from "../types";
import { RecordWeightDialog } from "./RecordWeightDialog";

type Props = {
  accountId: string;
  animal: AnimalDetail;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function labelForSource(source: "balance" | "tape"): string {
  return source === "balance" ? "Balanza" : "Cinta";
}

export function AnimalWeightSection({ accountId, animal }: Props) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useWeightHistory(accountId, animal.id);
  const records = data?.records ?? [];

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-app-ink-2" aria-hidden />
          <h2 className="font-semibold text-app-ink-1 text-base">Pesos</h2>
        </div>
        <RoleGate roles={["owner", "admin", "operator"]}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-9 items-center rounded-xl border border-app-border bg-app-surface px-3 font-medium text-[13px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
          >
            Registrar peso
          </button>
        </RoleGate>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="h-16 animate-pulse rounded-xl bg-app-surface-2" />
        ) : records.length === 0 ? (
          <p className="text-[13px] text-app-ink-3">
            Sin pesos registrados todavía. Es opcional — el sistema usa la tabla referencial cuando
            no hay peso reciente.
          </p>
        ) : (
          <ul className="divide-y divide-app-border rounded-xl border border-app-border bg-app-surface-2">
            {records.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <p className="font-medium text-[14px] text-app-ink-1">
                    {Number(r.weightKg).toFixed(1)} kg
                  </p>
                  <p className="mt-0.5 text-[12px] text-app-ink-3">
                    {formatDate(r.measuredAt)} · {labelForSource(r.source)}
                    {r.notes ? ` · ${r.notes}` : ""}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
                    r.source === "balance"
                      ? "bg-app-emerald/10 text-app-emerald"
                      : "bg-app-ink-3/10 text-app-ink-2"
                  }`}
                >
                  {labelForSource(r.source)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RecordWeightDialog
        accountId={accountId}
        animal={animal}
        open={open}
        onOpenChange={setOpen}
      />
    </section>
  );
}
